import { useEffect, useState } from "preact/hooks"
import { Button } from "../../../components/ui/button"
import { DecimalInput } from "../../../components/ui/decimal_input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Label } from "../../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import type { TimePeriod } from "../types"
import { line_setup_from_node, type LineSetup } from "../utils/line_setup"
import { format_period_header } from "../utils/periods"
import { cases_per_month_from_input, cases_per_store_input_value } from "./create_sales_source_dialog"
import { DoorsByMonthEditor } from "./doors_by_month_editor"
import { SuggestedPriceCaption } from "./suggested_price_caption"

export interface EditLineTarget {
  /** The leaf row as it sits in the forecast tree. */
  node: any
  /** `Customer › Product line › SKU`, for the title. */
  path_caption: string
  /** The forecast customer the line sits under, for the price suggestion. */
  customer_name: string | null
}

interface EditLineDialogProps {
  target: EditLineTarget | null
  periods: TimePeriod[]
  on_close: () => void
  on_save: (node_id: string, setup: LineSetup) => void
}

/**
 * Reopen a line's setup — the same fields Add new line asks for — and rewrite its plan months from
 * it (1st Phorm, 10/29: "can't edit a line after creating it"). Actual and locked months are kept;
 * the change is a draft like any grid edit until Save.
 */
export function EditLineDialog(props: EditLineDialogProps) {
  const { target, periods, on_close, on_save } = props
  const [setup, set_setup] = useState<LineSetup | null>(null)
  const weekly = periods[0]?.grain === "week"
  const period_span = weekly ? "week" : "month"

  useEffect(() => {
    set_setup(target ? line_setup_from_node(target.node, periods) : null)
  }, [target, periods])

  if (!target || !setup) return null

  function update<K extends keyof LineSetup>(field: K, value: LineSetup[K]): void {
    set_setup((prev: LineSetup | null) => (prev ? { ...prev, [field]: value } : prev))
  }

  const valid = setup.initial_doors > 0 && setup.cases_per_store_per_month > 0 && setup.cost_per_case > 0 && Boolean(setup.start_period)
  const product_id = target.node?.metadata?.catalog_product_id as string | undefined

  return (
    <Dialog open={target != null} onOpenChange={(open: boolean) => (open ? null : on_close())}>
      <DialogContent className="max-w-2xl gap-4">
        <DialogHeader>
          <DialogTitle>Edit line</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-600">{target.path_caption}</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Initial doors</Label>
            <DecimalInput value={setup.initial_doors} onValueChange={(value: number) => update("initial_doors", Math.max(0, Math.round(value)))} />
          </div>
          <div className="space-y-2">
            <Label>Cases per store per {period_span}</Label>
            <DecimalInput
              value={cases_per_store_input_value(setup.cases_per_store_per_month, weekly)}
              onValueChange={(value: number) => update("cases_per_store_per_month", cases_per_month_from_input(value, weekly))}
            />
          </div>
          <div className="space-y-2">
            <Label>Store count growth (% per year)</Label>
            <DecimalInput
              value={Math.round(setup.store_growth * 100 * 1e6) / 1e6}
              onValueChange={(pct: number) => update("store_growth", pct / 100)}
            />
          </div>
          <div className="space-y-2">
            <Label>Same-store sales growth (% per year)</Label>
            <DecimalInput
              value={Math.round(setup.same_store_growth * 100 * 1e6) / 1e6}
              onValueChange={(pct: number) => update("same_store_growth", pct / 100)}
            />
          </div>
          <div className="space-y-2">
            <Label>Price per case ($)</Label>
            <DecimalInput value={setup.cost_per_case} onValueChange={(price: number) => update("cost_per_case", price)} />
            <SuggestedPriceCaption
              customer={target.customer_name}
              product_id={product_id}
              current_price={setup.cost_per_case}
              on_apply={(price: number) => update("cost_per_case", price)}
            />
          </div>
          <div className="space-y-2">
            <Label>Start {period_span}</Label>
            <Select value={setup.start_period || undefined} onValueChange={(value: string) => update("start_period", value)}>
              <SelectTrigger>
                <SelectValue placeholder={`Select start ${period_span}`} />
              </SelectTrigger>
              <SelectContent>
                {periods.map((period: TimePeriod) => (
                  <SelectItem key={period.period_key} value={period.period_key}>
                    {format_period_header(period)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Doors by {period_span}</Label>
            <DoorsByMonthEditor
              initial_doors={setup.initial_doors}
              store_growth={setup.store_growth}
              start_period={setup.start_period}
              periods={periods}
              doors_by_period={setup.doors_by_period}
              on_change={(next: Record<string, number>) => update("doors_by_period", next)}
            />
          </div>
        </div>
        <p className="text-[11px] text-gray-500">
          Saving here rewrites this line's plan {period_span}s from the setup; actual and locked {period_span}s stay as they are.
          Nothing is written to the server until you Save the forecast.
        </p>
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={on_close}>
            Cancel
          </Button>
          <Button type="button" disabled={!valid} onClick={() => on_save(target.node.id, setup)}>
            Apply to line
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
